import { useState, useEffect } from "react";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import { Col, Container, Row } from "reactstrap";
import "../../assets/scss/pages/_settings.scss";
import SettingCard, { SettingWidgetsType } from "Components/Common/SettingCard";
import { useScreenTour } from "tour/useScreenTour";
import { tourElementsIds } from "tour/tourElementsIds";
import { useNavigate, useLocation } from "react-router-dom";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import Footer from "Layouts/Footer";
import { getStripeSetupLink } from "slices/thunks";
import { ErrorHandler } from "utils/toast";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";

const settingWidgets: SettingWidgetsType[] = [
  {
    id: 1,
    icon: "UserProfile",
    title: "Profile",
    subTitle:
      "Manage your company profile, including name, contact info, and logo.",
    url: "/profile",
  },
  {
    id: 2,
    icon: "Crown",
    title: "Subscription & Billing",
    subTitle: "Manage your plan, payments, and billing records",
    url: "/subscription",
  },
  {
    id: 3,
    icon: "TeamMember",
    title: "Team Members",
    subTitle:
      "Add or manage staff accounts and set their roles or access permissions.",
    url: "/team-members",
  },
  {
    id: 4,
    icon: "ConnectStripe",
    title: "Connect Stripe",
    subTitle: "Set up Stripe to start accepting payments from your clients.",
    url: undefined,
  },
  {
    id: 5,
    icon: "Section",
    title: "Sections",
    subTitle:
      "Save and manage reusable text blocks for quick insertion into your quotes and invoices.",
    url: "/sections",
  },
  {
    id: 6,
    icon: "Categories",
    title: "Categories",
    subTitle:
      "Organise your work by defining main categories for materials, labour, or services.",
    url: "/categories",
  },
  {
    id: 7,
    icon: "SubCategories",
    title: "Subcategories",
    subTitle:
      "Create sub-level groupings under categories for more precise Organisation.",
    url: "/subcategories",
  },
  {
    id: 8,
    icon: "Items",
    title: "Items",
    subTitle:
      "Add and manage individual items or services that appear in quotes and invoices.",
    url: "/items",
  },
  {
    id: 9,
    icon: "Payment",
    title: "Payments & Invoicing",
    subTitle:
      "Configure payment details, billing preferences, and quote/invoice settings.",
    url: "/payments-and-invoicing",
  },
  {
    id: 10,
    icon: "Notification",
    title: "Notifications Preference",
    subTitle:
      "Choose how and when you receive alerts about quotes, invoices, and jobs.",
    url: "/notifications-preference",
  },
  {
    id: 11,
    icon: "VideoTutorial",
    title: "Video Tutorials",
    subTitle:
      "Watch quick guides and walkthroughs to help you get the most out of EaziQuote.",
    url: "",
  },
  {
    id: 12,
    icon: "ChangePassword",
    title: "Change Password",
    subTitle: "Update your account password to keep your profile secure.",
    url: "/change-password",
  },
];

const STRIPE_CARD_ID = "settings-stripe-connect-card";

const Settings = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const dispatch = useAppDispatch();
  const settingsTour = useScreenTour("settings");
  const user = useAppSelector((state) => state.auth.user);
  const { firebaseConfig } = useAppSelector((state) => state.config);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeHighlight, setStripeHighlight] = useState(false);
  const [isShowStripeConnectModal, setIsShowStripeConnectModal] =
    useState(false);

  useEffect(() => {
    if (!locationState?.focusStripe) return;

    let highlightTimer: ReturnType<typeof setTimeout>;
    let clearTimer: ReturnType<typeof setTimeout>;

    // rAF ensures the DOM is painted before we try to find the element
    const rafId = requestAnimationFrame(() => {
      const el = document.getElementById(STRIPE_CARD_ID);
      if (!el) return;

      el.scrollIntoView({ behavior: "smooth", block: "center" });

      // Wait for smooth scroll to finish, then fire the pulse animation
      highlightTimer = setTimeout(() => {
        setStripeHighlight(true);
        // 3 iterations × 900ms + small buffer
        clearTimer = setTimeout(() => setStripeHighlight(false), 3000);
      }, 900);
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(highlightTimer);
      clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClick = (url?: string) => {
    if (url) {
      navigate(url);
    }
  };

  const handleStripeConnect = async () => {
    if (user?.stripe_account_status === "active") return;

    try {
      setStripeLoading(true);
      const result = await dispatch(getStripeSetupLink()).unwrap();
      const redirectUrl = result?.payload?.url;
      setIsShowStripeConnectModal(false);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setStripeLoading(false);
    }
  };

  document.title = "Settings | EaziQuote";

  return (
    <>
      <div className="page-content">
        <Container fluid>
          {/* Header */}
          <PageHeaderActions
            title="Settings"
            subtitle="Personalise your workspace and tools"
          // actions={
          //   user?.is_subscription_active
          //     ? []
          //     : [
          //         {
          //           label: "Subscribe",
          //           onClick: () => {
          //             if (user?.is_team_member) {
          //               ErrorToast(
          //                 "Only the account owner can manage subscriptions. Please contact your owner.",
          //               );
          //             } else {
          //               navigate("/subscribe-plan");
          //             }
          //           },
          //           leftIcon: <Icon name="CrownWhite" />,
          //         },
          //       ]
          // }
          />

          {/* Cards */}
          <Row className="g-4 mt-3" id={tourElementsIds.settingsTour1}>
            {settingWidgets
              .filter((item) =>
                item.id === 11
                  ? firebaseConfig?.videoTutorialUrl &&
                  firebaseConfig.videoTutorialUrl.length > 0
                  : true,
              )
              .map((item) => {
                const resolvedItem =
                  item.id === 11
                    ? { ...item, url: firebaseConfig?.videoTutorialUrl ?? "" }
                    : item;
                return (
                  <Col
                    key={resolvedItem.id}
                    id={resolvedItem.id === 4 ? STRIPE_CARD_ID : undefined}
                    xxl={3}
                    xl={3}
                    lg={4}
                    md={6}
                    sm={12}
                    xs={12}
                    className="d-flex d-md-flex d-sm-block mt-2"
                  >
                    <SettingCard
                      item={resolvedItem}
                      className={
                        resolvedItem.id === 4 && stripeHighlight
                          ? "stripe-highlight"
                          : undefined
                      }
                      onPress={
                        resolvedItem.id === 4
                          ? () => {
                            if (user?.stripe_account_status !== "active") {
                              setIsShowStripeConnectModal(true);
                            }
                          }
                          : resolvedItem.id === 11
                            ? () =>
                              window.open(
                                resolvedItem.url,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            : handleCardClick.bind(this, resolvedItem?.url)
                      }
                    />
                  </Col>
                );
              })}
          </Row>
        </Container>
        <OverlayGuide {...settingsTour} />
      </div>
      <Footer isSettingFooter firebaseConfig={firebaseConfig} />

      <RestrictedAccessModal
        isOpen={isShowStripeConnectModal}
        onCancel={() => setIsShowStripeConnectModal(false)}
        title="Payments & Processing Fees"
        description={`Online payments are securely processed through Stripe.\n\nA 0.5% platform processing fee applies to all card and online payments processed through Stripe, including deposit payments. Standard Stripe transaction fees may also apply separately.\n\nThese fees are deducted internally from your payout, your customers will always pay the exact invoice or deposit amount shown to them.\n\nPlatform fees do not apply to cash payments, bank transfers, or offline payments.`}
        buttonText="Connect Stripe"
        onButtonClick={handleStripeConnect}
        bodyClassName="text-start align-content-start"
        cancelText="Not Now"
        isLoading={stripeLoading}
      />
    </>
  );
};

export default Settings;
